export interface Application {
  id: string;
  applicationNumber: string;
  fullName: string;
  phone: string;
  phoneVerified: number;
  email: string | null;
  dateOfBirth: string | null;
  state: string;
  city: string;
  guardianName: string | null;
  alternatePhone: string | null;
  preferredContactMethod: string | null;
  highestQualification: string | null;
  educationStatus: string | null;
  boardOrUniversity: string | null;
  institutionName: string | null;
  stream: string | null;
  passingYear: number | null;
  percentageOrCgpa: string | null;
  entranceExamName: string | null;
  entranceExamScore: string | null;
  entranceExamRank: string | null;
  backlogStatus: string | null;
  preferredCourse: string;
  degreeLevel: string | null;
  preferredStates: string | null;
  preferredCities: string | null;
  collegeType: string | null;
  annualBudget: string | null;
  hostelRequired: number | null;
  educationLoanRequired: number | null;
  admissionSession: string | null;
  studyMode: string | null;
  additionalMessage: string | null;
  leadStatus: LeadStatus;
  assignedCounsellorId: string | null;
  serviceConsent: number;
  marketingConsent: number;
  consentVersion: string | null;
  consentAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export type LeadStatus =
  | 'New'
  | 'Verified'
  | 'Documents Pending'
  | 'Documents Received'
  | 'Counsellor Assigned'
  | 'Contacted'
  | 'Interested'
  | 'College Suggested'
  | 'Application Started'
  | 'Admission Completed'
  | 'Not Interested'
  | 'Invalid Lead'
  | 'Follow-up';

export const LEAD_STATUSES: LeadStatus[] = [
  'New',
  'Verified',
  'Documents Pending',
  'Documents Received',
  'Counsellor Assigned',
  'Contacted',
  'Interested',
  'College Suggested',
  'Application Started',
  'Admission Completed',
  'Not Interested',
  'Invalid Lead',
  'Follow-up',
];

export interface Document {
  id: string;
  applicationId: string;
  documentType: DocumentType;
  r2ObjectKey: string;
  originalFilename: string;
  storedFilename: string;
  mimeType: string;
  fileSize: number;
  uploadedAt: string;
}

export type DocumentType =
  | 'photograph'
  | 'class10_marksheet'
  | 'class12_marksheet'
  | 'entrance_scorecard';

export const DOCUMENT_TYPES: { value: DocumentType; label: string }[] = [
  { value: 'photograph', label: 'Student Photograph' },
  { value: 'class10_marksheet', label: 'Class 10 Marksheet' },
  { value: 'class12_marksheet', label: 'Class 12 / Latest Qualification Marksheet' },
  { value: 'entrance_scorecard', label: 'Entrance Exam Scorecard' },
];

export interface AdminUser {
  id: string;
  fullName: string;
  email: string;
  role: AdminRole;
  isActive: number;
  createdAt: string;
  updatedAt: string;
}

export type AdminRole = 'owner' | 'admin' | 'counsellor';

export interface LeadNote {
  id: string;
  applicationId: string;
  adminUserId: string;
  adminUserName?: string;
  note: string;
  createdAt: string;
}

export interface ActivityLog {
  id: string;
  applicationId: string | null;
  adminUserId: string | null;
  adminUserName?: string;
  action: string;
  metadata: string | null;
  createdAt: string;
}

export interface ContactRequest {
  id: string;
  name: string;
  phone: string | null;
  email: string | null;
  message: string;
  status: string;
  createdAt: string;
}

export interface DeletionRequest {
  id: string;
  applicationNumber: string;
  phone: string;
  email: string | null;
  reason: string | null;
  phoneVerified: number;
  status: string;
  createdAt: string;
  resolvedAt: string | null;
}

export interface ApiSuccess<T> {
  success: true;
  data: T;
}

export interface ApiError {
  success: false;
  error: {
    code: string;
    message: string;
    field?: string;
  };
}

export type ApiResponse<T> = ApiSuccess<T> | ApiError;

export interface PaginatedResponse<T> {
  items: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface DashboardStats {
  totalApplications: number;
  newApplications: number;
  verifiedLeads: number;
  documentsPending: number;
  contactedLeads: number;
  interestedLeads: number;
  admissionsCompleted: number;
  applicationsByCourse: { course: string; count: number }[];
  applicationsByState: { state: string; count: number }[];
  recentActivity: ActivityLog[];
}
